/**
 * 懒加载块的统一装载（21ai）。
 *
 * 此前 37 个 `*Lazy.tsx` 各自写了一份同样的 `void import(...).then(setMod)`，
 * **没有一个带 catch**。chunk 取不到时（404/断网/刚发版换了哈希/SW 缓存与服务器
 * 产物对不上）promise 直接 reject，`mod` 永远是 null，壳渲染 null——
 * 界面上什么都不会发生，也没有任何提示。人工报的「发送名片点了没反应」就是它：
 * 点击只是把 `cardPicking` 置 true，浮层在另一个块里，块没来就永远不出现，
 * 而且 state 已经是 true，再点也不会重挂载重试。
 *
 * 这里做三件事：
 * 1. **接住失败**，不再有未处理的 rejection；
 * 2. **自动重试**：chunk 取不到多半是瞬时的（发版换哈希、弱网），退避重试
 *    `MAX_RETRY` 次；
 * 3. 把 `failed` 透出来，让壳可以选择渲染可重试的占位（浮层类的壳通常仍渲染
 *    null，但至少调用方有得选，而不是被静默吞掉）。
 *
 * `load` 恒是调用处的内联箭头（`() => import('./X')`），每次渲染都是新引用，
 * 所以**刻意不进依赖数组**——进了就会每帧重装。
 */
import { useEffect, useState } from 'preact/hooks';

/** 最多再试几次（首次之外）。退避 400ms / 1200ms，够盖住发版切换的窗口 */
export const MAX_RETRY = 2;
export const RETRY_DELAYS: readonly number[] = [400, 1200];

/** 第 n 次失败后还要不要再试（n 从 0 起） */
export function shouldGiveUp(n: number): boolean {
  return n >= MAX_RETRY;
}

/** 第 n 次失败后的退避毫秒；越界兜底成最后一档，别把 undefined 传进 setTimeout */
export function nextRetryDelay(n: number): number {
  return RETRY_DELAYS[n] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1] ?? 1200;
}

export interface LazyModule<M> {
  /** null = 还没到（加载中或已失败） */
  readonly mod: M | null;
  /** 重试都用尽了仍失败 */
  readonly failed: boolean;
  /** 手动再试一次（壳渲染重试入口时用） */
  readonly retry: () => void;
}

export function useLazyModule<M>(load: () => Promise<M>): LazyModule<M> {
  const [mod, setMod] = useState<M | null>(null);
  const [failed, setFailed] = useState(false);
  const [round, setRound] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setFailed(false);

    const attempt = (n: number): void => {
      load().then(
        (m) => {
          if (!cancelled) setMod(m);
        },
        () => {
          if (cancelled) return;
          if (shouldGiveUp(n)) {
            setFailed(true);
            return;
          }
          timer = setTimeout(() => attempt(n + 1), nextRetryDelay(n));
        },
      );
    };
    attempt(0);

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
    // load 每次渲染都是新引用，进依赖会每帧重装；round 是手动重试的闸
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  return { mod, failed, retry: () => setRound((n) => n + 1) };
}
